import { useEffect, useRef } from 'react'
import type { StationStatusSnapshot } from '@metro/station-status'
import { StationCardView } from './StationCardView'
import { useStationSettled } from './useStationSettled'
import { resolveStatusVariant } from './statusMapping'
import { defaultStationCardCopy, type StationCardCopySet } from './copy'
import styles from './StationCardCarousel.module.css'

export interface StationCardCarouselCard {
  id: string
  name: string
  lineLocalName: string
  lineHexColor: string
  zone: string | null
  /**
   * TFL NaPTAN id — used to resolve the live status snapshot (spec §6).
   * Optional: cards without a naptanId are always shown as Open.
   */
  naptanId?: string
  /** `/<locale>/lines/<slug>` — the line link under the name; absent → plain text. */
  lineHref?: string
}

export interface StationCardCarouselProps {
  cards: ReadonlyArray<StationCardCarouselCard>
  currentId: string
  onSettled: (id: string) => void
  onFromTap: (stationId: string) => void
  onToTap: (stationId: string) => void
  onDetailsTap: (stationId: string) => void
  /**
   * Forwarded to each `<StationCardView>`'s `showRouteButtons`. The
   * carousel itself does not gate anything; it only threads the flag
   * through to per-slot views. Defaults to `true` for shape-stability
   * with isolated tests; production callers (`StationCard` →
   * `StationCardOrDetailsRouter`) wire this to the `routeBuilder`
   * feature flag so the buttons hide when the flag is off.
   */
  showRouteButtons?: boolean
  /**
   * Forwarded to each slot — fired when a sandwich card's status
   * banner is tapped. Status data plumbing through the carousel is
   * deferred (spec §6 — emit-only stub); kept here so isolated fixtures
   * can still wire the callback.
   */
  onStatusBannerTap?: (stationId: string) => void
  /**
   * Opens the line's view on the map from the line link under the station
   * name (Figma LineView E). Only cards with a `lineHref` show the link.
   */
  onLineTap?: (stationId: string) => void
  /**
   * Live status snapshot (naptanId-keyed) from the host's
   * StationStatus preloader (spec §6). Each slot resolves its
   * own status by looking up card.naptanId in this map. Absent
   * key = Open (no banner). Optional — defaults to empty.
   */
  statuses?: StationStatusSnapshot
  /** Localised copy. @defaultValue {@link defaultStationCardCopy} */
  copy?: StationCardCopySet
}

/**
 * Horizontal paged station-card carousel. Pixel-parity with iOS
 * `MetroModules/Sources/StationCard/Views/StationCardCollectionView.swift`:
 *
 *   ScrollView(.horizontal, showsIndicators: false)
 *     LazyHStack(spacing: ._8)
 *       ForEach(stations) { StationCardView(...) }
 *     .scrollTargetBehavior(.viewAligned(limitBehavior: .always))
 *     .safeAreaPadding(.horizontal, ._24)
 *     .frame(height: 244)
 *     .padding(.top, ._24)
 *
 * cardWidth = viewport − inset×2 (24pt × 2 = 48pt). Web maps the iOS
 * `.viewAligned(.always)` snap behaviour to CSS `scroll-snap-type: x
 * mandatory; scroll-snap-stop: always`.
 *
 * Settle detection (which card is centered after a swipe stops) lives in
 * `useStationSettled`, reading `data-card-id` on each slot. When the
 * settled id differs from the centered card, `onSettled(id)` fires.
 *
 * Programmatic scroll: when `currentId` changes from outside (e.g. map
 * tap), we snap the container to the matching slot.
 */
export function StationCardCarousel({
  cards,
  currentId,
  onSettled,
  onFromTap,
  onToTap,
  onDetailsTap,
  showRouteButtons = true,
  onStatusBannerTap,
  onLineTap,
  statuses = {},
  copy = defaultStationCardCopy,
}: StationCardCarouselProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  // Bug 4 fix: track the id we last emitted via `onSettled` so we can
  // tell user-driven settle (carousel reports id, parent updates
  // currentId) apart from external selection (map tap). Programmatic
  // `scrollIntoView` while the user is mid-swipe yanks the scroll
  // position and prevents reaching subsequent cards.
  const lastEmittedRef = useRef<string | null>(null)
  const handleSettled = (id: string) => {
    lastEmittedRef.current = id
    onSettled(id)
  }
  useStationSettled(
    containerRef,
    cards.map((c) => c.id),
    handleSettled,
    { debounceMs: 200 },
  )

  // Programmatic scroll: align the carousel to the externally-selected
  // card. We use `scrollIntoView({ inline: 'start' })` rather than
  // computing pixel offsets — the slot's scroll-snap-align lines it up
  // with the container's scroll-padding-inline. Skip when this update
  // came from our own settle event — the user is already scrolled
  // there and we'd be fighting their gesture.
  useEffect(() => {
    if (lastEmittedRef.current === currentId) {
      lastEmittedRef.current = null
      return
    }
    const container = containerRef.current
    // containerRef is attached to the rendered scroll container; this
    // guard only fires if the effect runs before commit, which React
    // doesn't allow. Defensive only.
    /* v8 ignore start */
    if (container === null) return
    /* v8 ignore stop */
    const target = container.querySelector<HTMLElement>(`[data-card-id="${currentId}"]`)
    if (target === null) return
    target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
  }, [currentId])

  return (
    <div
      ref={containerRef}
      role="region"
      aria-roledescription="carousel"
      data-testid="station-card-carousel"
      className={styles.carousel}
    >
      {cards.map((card, i) => (
        <div
          key={card.id}
          data-card-id={card.id}
          data-testid={`station-card-card-${card.id}`}
          className={styles.slot}
          role="group"
          aria-label={copy.carouselCardAria(i + 1, cards.length, card.name)}
          aria-current={card.id === currentId ? 'true' : undefined}
        >
          <StationCardView
            station={card}
            onFromTap={onFromTap}
            onToTap={onToTap}
            onDetailsTap={() => onDetailsTap(card.id)}
            showRouteButtons={showRouteButtons}
            onStatusBannerTap={onStatusBannerTap}
            onLineTap={onLineTap}
            status={resolveStatusVariant(card.naptanId, statuses)}
            copy={copy}
          />
        </div>
      ))}
    </div>
  )
}
